<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="600px" persistent>
        <v-card>
            <v-card-title>
                <span class="text-h5">{{ $t('Lataa kuva') }}</span>
            </v-card-title>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <v-text-field v-model="title" :label="$t('Otsikko')" :rules="[v => !!v || 'Otsikko on pakollinen']"
                        required outlined></v-text-field>

                    <v-file-input v-model="imageFile" :label="$t('Valitse kuva')" :rules="[
                        v => !!v || 'Kuva on pakollinen',
                        v => !v || v.size < 5000000 || 'Kuvan koko ei saa ylittää 5 MB'
                    ]" accept="image/*" prepend-icon="" prepend-inner-icon="mdi-camera" outlined show-size
                        @change="handleFileChange"></v-file-input>

                    <!-- Image Preview -->
                    <div v-if="imagePreview" class="mb-4">
                        <v-img :src="imagePreview" max-height="200" contain class="grey lighten-4"></v-img>
                    </div>

                    <v-alert v-if="error" type="error" dense outlined class="mt-3">
                        {{ error }}
                    </v-alert>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" text @click="close" :disabled="uploading">
                    {{ $t('Peruuta') }}
                </v-btn>
                <v-btn color="primary" @click="upload" :loading="uploading" :disabled="!valid || uploading">
                    {{ $t('Lataa ja lisää') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import adminApi from '@/api/admin'

export default {
    name: 'UploadDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        screenId: {
            type: [String, Number],
            required: true
        },
        position: {
            type: Number,
            required: true
        }
    },
    setup(props, { emit }) {
        const form = ref(null)
        const valid = ref(false)
        const title = ref('')
        const imageFile = ref(null)
        const imagePreview = ref(null)
        const uploading = ref(false)
        const error = ref(null)

        // Watch for dialog closing to reset form
        watch(() => props.value, (newValue) => {
            if (!newValue) {
                resetForm()
            }
        })

        const handleFileChange = (file) => {
            error.value = null
            if (file) {
                // Generate preview
                const reader = new FileReader()
                reader.onload = (e) => {
                    imagePreview.value = e.target.result
                }
                reader.readAsDataURL(file)
            } else {
                imagePreview.value = null
            }
        }

        const upload = async () => {
            if (!form.value.validate()) {
                return
            }

            uploading.value = true
            error.value = null

            try {
                const imageData = {
                    title: title.value,
                    imageBlob: imageFile.value,
                    imageName: imageFile.value.name
                }

                const response = await adminApi.uploadAndAddImage(
                    props.screenId,
                    imageData,
                    props.position
                )

                if (response.ok) {
                    emit('uploaded')
                    close()
                } else {
                    error.value = response.data?.error || 'Kuvan lataus epäonnistui'
                }
            } catch (err) {
                console.error('Upload error:', err)
                error.value = err.response?.data?.error || 'Kuvan lataus epäonnistui'
            } finally {
                uploading.value = false
            }
        }

        const close = () => {
            emit('input', false)
        }

        const resetForm = () => {
            if (form.value) {
                form.value.reset()
            }
            title.value = ''
            imageFile.value = null
            imagePreview.value = null
            error.value = null
            valid.value = false
        }

        return {
            form,
            valid,
            title,
            imageFile,
            imagePreview,
            uploading,
            error,
            handleFileChange,
            upload,
            close
        }
    }
}
</script>

<style scoped>
/* Add any custom styles here */
</style>
